<script setup>
import { computed, ref, watchEffect, reactive } from "vue";
import Swal from "sweetalert2";
import sendIcon from "@/assets/img/send.png";
import CalendarIcon from "@/assets/svg/calendar-icon.svg?component";
import SendCommentIcon from "@/assets/svg/send-comment-icon.svg?component";
import { useValidation } from "@composables/useValidation";
import { required, maxLength } from "@services/i18n-validators";
import { ElNotification } from "element-plus";
import DeleteIcon from "@/assets/svg/delete-icon.svg?component";
import { useUserStore } from '@store/user.js'

const userStore = useUserStore()


const emit = defineEmits(["reply", "resolve", "goto"]);
const props = defineProps({
    comment: { type: Object, required: true },
    replies: { type: Array, default: () => [] },
    showActions: { type: Boolean, default: false },
    showPageMeta: { type: Boolean, default: false },
    replyPlaceholder: { type: String, default: "Add Reply" },
    isClosed: { type: Boolean, default: false },
});

const form = reactive({ reply: "" });
const localReplies = ref([]);
const isSubmitting = ref(false);
const deleteModal = ref([false]);
const deleteItem = ref({ type: '', id: null });

const { v$ } = useValidation(
    reactive({ reply: { required, maxLength: maxLength(1000) } }),
    form
);

watchEffect(() => {
    const list = Array.isArray(props.replies)
        ? props.replies
        : props.comment?.replies || [];
    localReplies.value = list
        .slice()
        .sort(
            (a, b) =>
                new Date(b?.created_at || b?.ts || 0) -
                new Date(a?.created_at || a?.ts || 0)
        );
});

const authorName = computed(() => props.comment?.user?.name || "Anonymous");
const formattedDate = computed(() => formatDate(props.comment?.created_at));
const avatarUrl = computed(() => props.comment?.user?.avatar?.url || null);
const initials = computed(() =>
    (authorName.value || "U").slice(0, 1).toUpperCase()
);

function submitReply() {
    if (!v$.value.$validate()) {
        ElNotification({
            title: "Validation",
            message: "Please enter a reply (max 1000 characters).",
            type: "warning",
            position: "bottom-left",
        });
        return;
    }
    const text = (form.reply || "").trim();
    if (!text) return;
    if (text.length > 1000) {
        return;
    }
    isSubmitting.value = true;
    const done = () => {
        isSubmitting.value = false;
    };
    // Emit both keys for backward/forward compatibility with backend handlers
    emit("reply", { reply: text, text, comment: props.comment, done });
    form.reply = "";
    if (v$ && v$.value) v$.value.$reset();
}

function formatDate(d) {
    if (!d) return "";
    try {
        const dt = new Date(d);
        return (
            dt.toLocaleString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
            }) +
            ", " +
            dt.toLocaleDateString()
        );
    } catch {
        return "";
    }
}

async function confirmResolve() {
    const res = await Swal.fire({
        title: "Resolve this comment?",
        text: "This will mark the comment as resolved and hide it from the list.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, resolve",
        cancelButtonText: "Cancel",
    });
    if (res.isConfirmed) {
        emit("resolve", { comment: props.comment });
    }
}

function getReplyName(r) {
    const raw = r && (r.user?.name ?? r.user);
    return typeof raw === "string" && raw.trim()
        ? raw
        : r?.user?.name || "User";
}

function getReplyInitial(r) {
    const name = getReplyName(r);
    const initial = name && typeof name === "string" ? name.charAt(0) : "U";
    return initial.toUpperCase();
}

// Render context with highlighted mentions similar to Slack
const renderedContext = computed(() => {
    const raw = (props.comment?.context || "").toString();
    const mentions = Array.isArray(props.comment?.mentions)
        ? props.comment.mentions
        : [];
    if (!raw) return "";
    // Basic escape to prevent HTML injection
    const escapeHtml = (s) =>
        s
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#039;");
    let text = escapeHtml(raw);
    // Highlight tokens that start with @ and match any mention name
    if (mentions.length > 0) {
        const names = mentions
            .filter((n) => typeof n === "string" && n.trim().length)
            .map((n) => n.trim());
        // Sort by length desc to avoid partial overlaps
        names.sort((a, b) => b.length - a.length);
        for (const name of names) {
            // Match @Name where Name is the exact user name, boundaries to avoid mid-word
            const pattern = new RegExp(
                `(^|\\s)@${name.replace(
                    /[-/\\^$*+?.()|[\]{}]/g,
                    "\\$&"
                )}(?=$|[\n\r\t .,!?;:])`,
                "g"
            );
            text = text.replace(
                pattern,
                (m, p1) =>
                    `${p1}<span style=\"color: #1D4ED8; font-weight: 700;\">@${name}</span>`
            );
        }
    } else {
        // Fallback: style any @word token
        text = text.replace(
            /(^|\s)@([\p{L}0-9_.-]+)/gu,
            '$1<span style="color: #1D4ED8; font-weight: 700;">@$2</span>'
        );
    }
    return text;
});

function deleteComment(type, id) {
    deleteItem.value = { type, id };
    deleteModal.value[0] = true;
}

function confirmDelete() {
    deleteModal.value[0] = false;
    try {
        emit("deleteComment", deleteItem.value);
        ElNotification({
            title: 'Success',
            message: `${deleteItem.value.type === 'comment' ? 'Comment' : 'Reply'} deleted successfully`,
            type: 'success',
            position: 'bottom-left',
        });
    } catch (error) {
        console.error('Error deleting comment:', error);
        ElNotification({
            title: 'Error',
            message: 'Failed to delete comment',
            type: 'error',
            position: 'bottom-left',
        });
    }
}
</script>

<template>
    <div class="comment-card" :class="{ closed: isClosed }">
        <div class="card-header">
            <div class="user-info">
                <img
                    v-if="avatarUrl"
                    :src="avatarUrl"
                    class="user-avatar-img"
                    alt="avatar"
                />
                <div v-else class="user-avatar">{{ initials }}</div>
                <div class="user-details">
                    <strong class="user-name">{{ authorName }}</strong>
                    <small class="timestamp">
                        <CalendarIcon />
                        {{ formattedDate }}
                    </small>
                </div>
            </div>
            <div class="actions-right" v-if="!isClosed">
                <button
                    class="resolve-btn"
                    @click="confirmResolve"
                    title="Resolve"
                >
                    Mark As Resolved
                    <img
                        width="20"
                        src="@/assets/img/checkmark-square-02.png"
                        alt=""
                    />
                </button>
            </div>
        </div>

        <div class="card-content">
            <div class="row">
                <div class="col-11">
                    <div class="comment-text" v-html="renderedContext" ></div>
                </div>
                <div class="col-1">
                    <DeleteIcon v-if="userStore.user.roles.includes('Super Admin')" @click="deleteComment('comment',comment.id)" :style="{ cursor: 'pointer' }" />
                </div>
            </div>
            
            <slot name="preview"></slot>
        </div>
        <div class="add-reply" v-if="!isClosed">
            <div class="input-wrap">
                <input
                    v-model="form.reply"
                    type="text"
                    class="reply-input"
                    :placeholder="replyPlaceholder"
                    :disabled="isSubmitting"
                    @keyup.enter="submitReply"
                />
                <div v-if="v$?.reply?.$error" class="error-text">
                    <small
                        v-for="e in v$.reply.$errors"
                        :key="e.$uid"
                        class="text-danger"
                    >
                        {{ e.$message }}
                    </small>
                </div>
            </div>
            <button
                class="send-btn"
                :disabled="isSubmitting"
                @click="submitReply"
            >
                <SendCommentIcon />
                <!-- <img :src="sendIcon" alt="send" /> -->
            </button>
        </div>
        <div v-else class="closed-banner">Comment closed</div>
        <div class="replies" v-if="localReplies.length">
            <div v-for="r in localReplies" :key="r.id" class="reply">
                <div class="card-header">
                    <div class="user-info">
                        <div class="reply-avatar">{{ getReplyInitial(r) }}</div>
                        <div class="user-details">
                            <strong class="user-name">{{
                                getReplyName(r)
                            }}</strong>
                            <small class="timestamp">{{
                                formatDate(r.created_at || r.ts)
                            }}</small>
                        </div>
                    </div>
                </div>
                <div class="card-content">
                    <div class="row">
                        <div class="col-11">
                            <div class="comment-text">{{ r.reply || r.text }}</div>
                        </div>
                        <div class="col-1">
                            <DeleteIcon v-if="userStore.user.roles.includes('Super Admin')" @click="deleteComment('reply',r.id)" :style="{ cursor: 'pointer' }" />
                        </div>
                    </div>
                    
                </div>
            </div>
        </div>
    </div>

    <!-- Delete Modal -->
    <DeleteModal
        class="delete-modal"
        :show="deleteModal[0]"
        @close="deleteModal[0] = false"
        :text="deleteItem.type"
        
        @save="confirmDelete"
    />
</template>



<style scoped>
.add-reply {
    display: flex;
    align-items: center;
    gap: 8px;
}
.input-wrap {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
}
.error-text {
    margin-top: 4px;
    line-height: 1.2;
}
.error-text .text-danger {
    font-size: 0.8rem;
    display: block;
}
</style>
